/**
 * Admin Operations API Service
 *
 * Thin wrapper over the `/admin/operations/*` endpoints that power the
 * Admin "Scheduled Items Overview" dashboard at `APP_ROUTES.adminOperations`.
 *
 * Three responsibilities live here:
 *
 *   1. `fetchOperationEvents` — cursor-paginated, privacy-filtered timeline of
 *      upcoming or past scheduled e-cards and flower-order placements. The DTO
 *      never carries last names, e-card body, or e-card subject; user-uploaded
 *      card imagery is redacted server-side (`thumbnailHidden`). Flower
 *      delivery dates stay out of the list payload; deeper context loads through
 *      the audited drill-in modals.
 *   2. `sendCardAsAdmin` — admin override of the user-facing Send Now action.
 *      Mirrors `scheduledCardsService.sendScheduledCardNow` (forwards the
 *      browser origin via `X-Frontend-Base-Url`) so the recipient's view-card
 *      link points at the same environment the admin is on.
 *   3. `cancelCardAsAdmin` — admin cancel for pending/failed e-cards on behalf
 *      of the customer (same status rules as user cancel, no ownership filter).
 *   4. `placeFlowerOrderAsAdmin` — admin override that runs the same Florist
 *      One placement pipeline the scheduler uses, after the backend's three
 *      pre-flight guards (status, no existing VendorOrderId, delivery date
 *      within `FloristOneLimits.MaxDeliveryDaysAhead`).
 *
 * Keeping these wrappers in one file means the page and detail modal share one
 * import and one type definition for the unified event row.
 */

import apiClient from './apiClient';

/**
 * Discriminator string returned by the API for each row kind. The values mirror
 * the backend `AdminOperationEventKind` enum names exactly so consumers can
 * `switch` on `event.kind` without remapping casing.
 */
export const OPERATION_EVENT_KIND = {
  Email: 'Email',
  FlowerOrder: 'FlowerOrder',
  MailedCardOrder: 'MailedCardOrder',
  GiftOrder: 'GiftOrder',
} as const;

/** Tagged-union helper: one of the kinds above. */
export type OperationEventKind =
  (typeof OPERATION_EVENT_KIND)[keyof typeof OPERATION_EVENT_KIND];

/**
 * One row in the operations timeline. Mirrors `AdminOperationEventDto` on the
 * backend. See the C# DTO XML doc comments for the privacy contract — only
 * first names, status, kind, and the IDs needed for drill-in are exposed.
 */
export interface AdminOperationEvent {
  /** Synthetic stable id shaped as `{kind}-{id}`. */
  id: string;
  /** `Email`, `FlowerOrder`, or `MailedCardOrder`. */
  kind: OperationEventKind;
  /** ISO UTC timestamp the scheduler will (or did) act on this row. */
  eventTimestamp: string;
  /** B2C user id of the customer that owns this row. */
  userId: string;
  /** First name of the customer (server-side stripped of last name). */
  userFirstName: string;
  /** First name of the contact for e-card events; null otherwise. */
  contactFirstName: string | null;
  /** First name of the recipient for paid-order events; null otherwise. */
  recipientFirstName: string | null;
  /** Display name of the event type (e.g. "Birthday"). */
  eventTypeName: string;
  /** E-card `ScheduledItemStatus` or flower `Fulfillment/Payment` combined string. */
  status: string;
  /** Florist One order number once the order has been placed; null otherwise. */
  VendorOrderId: string | null;
  /** ScheduledItem.Id for both kinds — drives the audit-feed query. */
  scheduledItemId: number;
  /** FlowerOrder.Id for flower events; null otherwise. */
  flowerOrderId: number | null;
  /** MailedCardOrder.Id for mailed-card events; null otherwise (PBI #1222). */
  mailedCardOrderId: number | null;
  /** GiftOrder.Id for gift events; null otherwise (PBI #1527). */
  giftOrderId: number | null;
  /** ScheduledCard.Id for e-card events; null for flower orders. */
  cardId: number | null;
  /**
   * Catalog/admin-curated media token. Present only when the row safely
   * surfaces an admin-curated thumbnail (template token, no user override).
   * Resolve via `getMediaUrl(token)` from `cardTemplatesService`.
   */
  thumbnailMediaToken: string | null;
  /**
   * External thumbnail URL — used for catalog template `PreviewImageUrl`
   * (e-cards without a media token) and for `FlowerOrder.ImageUrl` (catalog
   * product image snapshot from Florist One). Always admin-safe content.
   * For video e-cards this may be the HTML video poster still URL.
   */
  thumbnailExternalUrl: string | null;
  /**
   * When true, `thumbnailMediaToken` is a video blob — render a muted video
   * thumb instead of an img (matches user Card Preview source).
   */
  thumbnailIsVideo: boolean;
  /**
   * `true` only when the row carries a user-uploaded card image override.
   * The dashboard renders a forbidden-circle indicator instead of the image
   * to keep user content out of the admin view. When this flag is true, the
   * server guarantees both `thumbnailMediaToken` and `thumbnailExternalUrl`
   * are null — there is no "I have it but won't show it" state.
   */
  thumbnailHidden: boolean;
  /** True when the item was auto-scheduled (vs manually created). */
  isAutoScheduled: boolean;
  /**
   * True when this row failed but is still being retried automatically (PBI #1222).
   * Derived server-side so the timeline agrees with the audit trail.
   */
  isRetrying: boolean;
  /** Attempts made so far when `isRetrying` is true; zero otherwise. */
  retryAttemptCount: number;
}

/** Cursor-paginated response from `GET /admin/operations/events`. */
export interface AdminOperationEventListResult {
  /** Rows for the current slice. */
  items: AdminOperationEvent[];
  /**
   * Opaque cursor for the next slice; null when the timeline is exhausted in
   * the requested direction. Pass back as the `cursor` parameter to fetch
   * the next slice.
   */
  nextCursor: string | null;
}

/** Direction of the operations timeline. */
export type OperationDirection = 'upcoming' | 'past';

/** Kind filter for the operations timeline. */
export type OperationKindFilter = 'all' | 'email' | 'flower' | 'mailedcard';

/** Query parameters accepted by `fetchOperationEvents`. */
export interface FetchOperationEventsParams {
  /** Whether to return upcoming or past events (default: `upcoming`). */
  direction?: OperationDirection;
  /** Restrict to a single event kind (default: `all`). */
  kind?: OperationKindFilter;
  /**
   * Optional exact-match status filter. Accepts `ScheduledItemStatus` values for
   * e-cards or individual `FulfillmentStatus` / `PaymentStatus` enum names for
   * flowers (backend parses each independently).
   */
  status?: string | null;
  /** Optional exact-match owning customer id filter. */
  userId?: string | null;
  /**
   * Optional case-insensitive substring match on the recipient/contact name.
   * Server-side matches first OR last name; only first names round-trip in
   * the response payload.
   */
  recipient?: string | null;
  /**
   * When set, restricts both e-card and flower streams to auto-scheduled
   * (true) or manual-only (false) rows. Omit for no filter.
   */
  isAutoScheduled?: boolean;
  /**
   * Opaque cursor returned from the previous request, or null/undefined for
   * the first page.
   */
  cursor?: string | null;
  /** Page size (default: 30, max: 100). */
  pageSize?: number;
  /** AbortSignal so the page can cancel in-flight requests on filter changes. */
  signal?: AbortSignal;
}

/**
 * Loads one slice of the unified scheduled-items timeline.
 *
 * The endpoint applies all privacy filtering server-side; this client
 * simply forwards query params and returns the typed cursor result.
 */
export const fetchOperationEvents = async (
  params: FetchOperationEventsParams = {}
): Promise<AdminOperationEventListResult> => {
  const queryParams: Record<string, string> = {};

  if (params.direction) queryParams.direction = params.direction;
  if (params.kind) queryParams.kind = params.kind;
  if (params.status) queryParams.status = params.status;
  if (params.userId) queryParams.userId = params.userId;
  if (params.recipient) queryParams.recipient = params.recipient;
  if (params.isAutoScheduled !== undefined) {
    queryParams.isAutoScheduled = params.isAutoScheduled ? 'true' : 'false';
  }
  if (params.cursor) queryParams.cursor = params.cursor;
  if (params.pageSize != null) queryParams.pageSize = params.pageSize.toString();

  const response = await apiClient.get<AdminOperationEventListResult>(
    '/admin/operations/events',
    {
      params: queryParams,
      signal: params.signal,
    }
  );

  return response.data;
};

/**
 * Admin override Send Now. Posts to
 * `POST /admin/operations/scheduled-cards/{id}/send`, forwarding the browser
 * origin via the `X-Frontend-Base-Url` header so the recipient view-card link
 * points at the same environment the admin is operating in.
 */
export const sendCardAsAdmin = async (scheduledCardId: number): Promise<void> => {
  const frontendBaseUrl =
    typeof window !== 'undefined' ? window.location.origin : undefined;

  await apiClient.post(
    `/admin/operations/scheduled-cards/${scheduledCardId}/send`,
    undefined,
    frontendBaseUrl
      ? { headers: { 'X-Frontend-Base-Url': frontendBaseUrl } }
      : undefined
  );
};

/**
 * Admin cancel for a pending/failed e-card on behalf of the customer.
 * Posts to `POST /admin/operations/scheduled-cards/{id}/cancel`. Sent cards
 * are rejected server-side (same rule as the user cancel endpoint).
 */
export const cancelCardAsAdmin = async (scheduledCardId: number): Promise<void> => {
  await apiClient.post(
    `/admin/operations/scheduled-cards/${scheduledCardId}/cancel`
  );
};

/** Successful response shape from the place-order endpoint. */
export interface PlaceFlowerOrderResponse {
  message: string;
  finalStatus: string;
}

/**
 * Admin override Place Order Now for flower pre-orders. The backend enforces
 * three pre-flight guards (status, no existing VendorOrderId, delivery
 * date within Florist One's forward window) and refuses with HTTP 409 when
 * any guard fails — those rejections are mapped to a typed error by the
 * caller via the standard apiClient error envelope.
 */
export const placeFlowerOrderAsAdmin = async (
  flowerOrderId: number
): Promise<PlaceFlowerOrderResponse> => {
  const response = await apiClient.post<PlaceFlowerOrderResponse>(
    `/admin/operations/flower-orders/${flowerOrderId}/place`
  );
  return response.data;
};

/** Admin Place Order Now for gift pre-orders (operations dashboard). */
export const placeGiftOrderAsAdmin = async (
  giftOrderId: number
): Promise<{ message: string; finalFulfillmentStatus: string; finalPaymentStatus: string }> => {
  const response = await apiClient.post(
    `/admin/operations/gift-orders/${giftOrderId}/place`
  );
  return response.data;
};
